import { mdiToolboxOutline } from "@mdi/js";

import { CtcServiceError } from "./ctc-model";

const ANALYZE_ENDPOINT = "/api/ai-factory/ctc/products/analyze";
const SITES_ENDPOINT = "/api/ai-factory/ctc/sites";
const SITE_SETTINGS_ENDPOINT = "/api/ai-factory/ctc/settings/sites";
const OFFER_CHECK_ENDPOINT = "/api/ai-factory/ctc/products/offer-check";
const SIMILAR_PRODUCTS_ENDPOINT = "/api/ai-factory/ctc/products/similar";
const ARTICLES_ENDPOINT = "/api/ai-factory/ctc/articles";
const CHECKS_ENDPOINT = "/api/ai-factory/ctc/affilizz-checks";
const OPPORTUNITIES_ENDPOINT = "/api/ai-factory/ctc/opportunities";

const errorDetail = (error) => error?.response?.data?.detail || {};

const toUiProduct = (product) => ({
  ...product,
  id: `${product.merchant}-${product.merchantProductId}`,
  visual: { icon: mdiToolboxOutline, color: "#6366F1" },
  features: Array.isArray(product.features) ? product.features : [],
  provenance: product.provenance || {},
});

const toUiOpportunity = (opportunity) => ({
  ...opportunity,
  product: toUiProduct(opportunity.product),
});

/**
 * Backend calls of the CTC assistant. Every record comes from the API: sites,
 * product analysis, RMR drafts, Affilizz contents and opportunities. The
 * backend hands a ready draft to AifPostGeneration and returns its state.
 */
export function createCtcService({ axiosGet, axiosPatch, axiosPost }) {
  const serviceError = (error, code, message) => {
    if (error instanceof CtcServiceError) return error;
    const {
      code: detailCode,
      message: detailMessage,
      ...details
    } = errorDetail(error);
    return new CtcServiceError(
      detailCode || code,
      detailMessage || message,
      details
    );
  };

  const merchantProductIdOf = (product) => {
    if (!product || !product.merchant || !product.merchantProductId) {
      throw new CtcServiceError(
        "invalid_product",
        "Le produit ne contient pas d’identifiant marchand exploitable."
      );
    }
    return product.merchantProductId;
  };

  const requireArticle = (response, code, message) => {
    if (!response?.data?.id) throw new CtcServiceError(code, message);
    return response.data;
  };

  return {
    async analyzeSource(url, siteId) {
      try {
        const response = await axiosPost(
          ANALYZE_ENDPOINT,
          { url: url.trim(), siteId },
          { timeout: 35000, skipErrorSnackbar: true }
        );
        const returnedError = response?.response?.data?.detail;
        if (returnedError?.code) {
          throw new CtcServiceError(
            returnedError.code,
            returnedError.message || "La fiche produit est introuvable."
          );
        }
        if (!response?.data?.merchantProductId) {
          throw new CtcServiceError(
            "analysis_failed",
            "La réponse du serveur ne contient pas de fiche produit exploitable."
          );
        }
        return toUiProduct(response.data);
      } catch (error) {
        if (error instanceof CtcServiceError) throw error;
        const detail = errorDetail(error);
        const timedOut = error?.code === "ECONNABORTED";
        throw new CtcServiceError(
          timedOut ? "timeout" : detail.code || "analysis_failed",
          timedOut
            ? "L’analyse du produit a dépassé 35 secondes. Réessayez dans quelques instants."
            : detail.message ||
              "La fiche produit n’a pas pu être récupérée. Réessayez plus tard."
        );
      }
    },
    async listSites() {
      try {
        const response = await axiosGet(SITES_ENDPOINT, undefined, {
          skipErrorSnackbar: true,
        });
        if (!Array.isArray(response?.data)) {
          throw new CtcServiceError(
            "sites_failed",
            "La réponse du serveur ne contient pas de sites CTC exploitables."
          );
        }
        return response.data;
      } catch (error) {
        throw serviceError(
          error,
          "sites_failed",
          "Les sites CTC n'ont pas pu être chargés."
        );
      }
    },
    async listSiteSettings({ page = 1, limit = 25, search, readiness } = {}) {
      try {
        const response = await axiosGet(
          SITE_SETTINGS_ENDPOINT,
          {
            page,
            limit,
            search: search || undefined,
            readiness: readiness || undefined,
          },
          { skipErrorSnackbar: true }
        );
        if (!Array.isArray(response?.data?.items)) {
          throw new CtcServiceError(
            "site_settings_failed",
            "La réponse du serveur ne contient pas de configuration exploitable."
          );
        }
        return response.data;
      } catch (error) {
        throw serviceError(
          error,
          "site_settings_failed",
          "La configuration des sites CTC n'a pas pu être chargée."
        );
      }
    },
    async updateSiteSetting(siteId, setting) {
      try {
        const response = await axiosPatch(
          `${SITE_SETTINGS_ENDPOINT}/${siteId}`,
          {
            defaultSignature: setting.defaultSignature,
            isEnabled: setting.isEnabled,
          }
        );
        if (!response?.data?.id) {
          throw new CtcServiceError(
            "site_setting_update_failed",
            "Le serveur n'a pas renvoyé la configuration enregistrée."
          );
        }
        return response.data;
      } catch (error) {
        throw serviceError(
          error,
          "site_setting_update_failed",
          "La configuration du site n'a pas pu être enregistrée."
        );
      }
    },
    /** Read-only check after the analysis: nothing is created in Affilizz. */
    async checkOffer({ product, siteId }) {
      try {
        const response = await axiosPost(
          OFFER_CHECK_ENDPOINT,
          {
            siteId,
            merchant: product.merchant,
            merchantProductId: merchantProductIdOf(product),
            productLocalizedId: product.productLocalizedId || undefined,
          },
          { timeout: 35000, skipErrorSnackbar: true }
        );
        const returnedError = response?.response?.data?.detail;
        if (returnedError?.code) {
          throw new CtcServiceError(
            returnedError.code,
            returnedError.message || "La recherche Affilizz a échoué."
          );
        }
        if (typeof response?.data?.hasOffer !== "boolean") {
          throw new CtcServiceError(
            "offer_check_failed",
            "La réponse du serveur ne contient pas de recherche d’offres exploitable."
          );
        }
        return response.data;
      } catch (error) {
        throw serviceError(
          error,
          "offer_check_failed",
          "La recherche Affilizz a échoué."
        );
      }
    },
    /** Read-only Affilizz lookup; mediaId is resolved exclusively by the API. */
    async findSimilarProducts({ product, siteId, query }) {
      try {
        const response = await axiosPost(
          SIMILAR_PRODUCTS_ENDPOINT,
          {
            siteId,
            merchant: product.merchant,
            merchantProductId: merchantProductIdOf(product),
            title: product.title,
            brand: product.brand || undefined,
            productLocalizedId: product.productLocalizedId || undefined,
            query: query || undefined,
          },
          { timeout: 120000, skipErrorSnackbar: true }
        );
        if (!Array.isArray(response?.data?.items)) {
          throw new CtcServiceError(
            "similar_products_failed",
            "La réponse Affilizz ne contient pas de produits similaires exploitables."
          );
        }
        return response.data.items;
      } catch (error) {
        throw serviceError(
          error,
          "similar_products_failed",
          "Les produits similaires Affilizz n'ont pas pu être récupérés."
        );
      }
    },
    async listArticles({ page = 1, limit = 25, search } = {}) {
      try {
        const response = await axiosGet(
          ARTICLES_ENDPOINT,
          { page, limit, search: search || undefined },
          { skipErrorSnackbar: true }
        );
        if (!Array.isArray(response?.data?.items)) {
          throw new CtcServiceError(
            "articles_failed",
            "La réponse du serveur ne contient pas de brouillons exploitables."
          );
        }
        return response.data;
      } catch (error) {
        throw serviceError(
          error,
          "articles_failed",
          "Les brouillons RMR n'ont pas pu être chargés."
        );
      }
    },
    /**
     * Phase 1: creates the final Affilizz contents of the selected product
     * and widget, and returns the affiliation of each. The requestId makes a
     * repeated call return the same verification.
     *
     * @param {Object} request - buildSelection() of the create flow
     */
    async verifyAffilizz(request) {
      try {
        const response = await axiosPost(
          CHECKS_ENDPOINT,
          {
            requestId: request.requestId,
            siteId: request.siteId,
            merchant: request.product.merchant,
            merchantProductId: merchantProductIdOf(request.product),
            productLocalizedId: request.product.productLocalizedId || undefined,
            title: request.product.title,
            productSnapshot: request.productSnapshot,
            widgetEnabled: request.widgetEnabled,
            widgetType: request.widgetType,
            similarProducts: request.similarProducts,
            opportunityId: request.opportunityId || undefined,
            replacesRequestId: request.replacesRequestId || undefined,
          },
          { timeout: 120000, skipErrorSnackbar: true }
        );
        return requireArticle(
          response,
          "affilizz_check_failed",
          "Le serveur n'a pas renvoyé la vérification Affilizz."
        );
      } catch (error) {
        throw serviceError(
          error,
          "affilizz_check_failed",
          "La vérification Affilizz n'a pas pu être effectuée."
        );
      }
    },
    /**
     * Abandons a pending verification of the current user: its opportunity
     * is released; the Affilizz contents already created stay orphaned.
     */
    async cancelAffilizzCheck(requestId) {
      try {
        await axiosPost(
          `${CHECKS_ENDPOINT}/cancel`,
          { requestId },
          { timeout: 30000, skipErrorSnackbar: true }
        );
      } catch (error) {
        throw serviceError(
          error,
          "affilizz_check_cancel_failed",
          "La vérification Affilizz n’a pas pu être annulée."
        );
      }
    },
    /**
     * The current manager's verifications not finalized yet (they survive a
     * reload), each with its product rebuilt from the verified snapshot.
     */
    async listPendingVerifications() {
      try {
        const response = await axiosGet(CHECKS_ENDPOINT, undefined, {
          skipErrorSnackbar: true,
        });
        return (Array.isArray(response?.data) ? response.data : []).map(
          (article) => ({
            ...article,
            product: toUiProduct({ ...article.productSnapshot }),
          })
        );
      } catch (error) {
        throw serviceError(
          error,
          "affilizz_checks_unavailable",
          "Les créations à reprendre n’ont pas pu être chargées."
        );
      }
    },
    /** Keeps the verified CTA and drops the widget, recreating nothing. */
    async disableAffilizzWidget(id) {
      try {
        const response = await axiosPost(
          `${CHECKS_ENDPOINT}/${id}/disable-widget`,
          undefined,
          { timeout: 30000, skipErrorSnackbar: true }
        );
        return requireArticle(
          response,
          "affilizz_widget_disable_failed",
          "Le serveur n'a pas renvoyé la vérification Affilizz."
        );
      } catch (error) {
        throw serviceError(
          error,
          "affilizz_widget_disable_failed",
          "Le widget n’a pas pu être retiré de la vérification."
        );
      }
    },
    /** Reads the affiliation of the same Affilizz contents again. */
    async recheckAffiliation(id) {
      try {
        const response = await axiosPost(
          `${CHECKS_ENDPOINT}/${id}/recheck`,
          undefined,
          { timeout: 60000, skipErrorSnackbar: true }
        );
        return requireArticle(
          response,
          "affilizz_recheck_failed",
          "Le serveur n'a pas renvoyé la vérification Affilizz."
        );
      } catch (error) {
        throw serviceError(
          error,
          "affilizz_recheck_failed",
          "La vérification de l’affiliation n’a pas pu être relancée."
        );
      }
    },
    /**
     * Phase 2: the editorial fields of a verified draft; the backend then
     * hands the same draft and Affilizz contents to the generation.
     */
    async finalizeArticle(id, request) {
      try {
        const response = await axiosPost(
          `${ARTICLES_ENDPOINT}/${id}/finalize`,
          {
            title: request.title,
            productSnapshot: request.productSnapshot,
            signature: request.signature,
            postStatus: request.postStatus,
            scheduledAt: request.scheduledAt || undefined,
          },
          { timeout: 120000, skipErrorSnackbar: true }
        );
        return requireArticle(
          response,
          "article_launch_failed",
          "Le serveur n'a pas renvoyé d'identifiant de brouillon RMR."
        );
      } catch (error) {
        throw serviceError(
          error,
          "article_launch_failed",
          "Le brouillon RMR n'a pas pu être enregistré."
        );
      }
    },
    /**
     * Resumes the Affilizz contents of a draft (failed or interrupted LINK,
     * then widget), then queues or re-checks its generation. It never creates
     * a second draft, publication or AIF request.
     */
    async retryAffilizz(id) {
      try {
        const response = await axiosPost(
          `${ARTICLES_ENDPOINT}/${id}/affilizz/retry`,
          undefined,
          { timeout: 120000, skipErrorSnackbar: true }
        );
        return requireArticle(
          response,
          "affilizz_retry_failed",
          "Le serveur n'a pas renvoyé le brouillon RMR."
        );
      } catch (error) {
        throw serviceError(
          error,
          "affilizz_retry_failed",
          "Les contenus Affilizz n'ont pas pu être réessayés."
        );
      }
    },
    /** New requested CMS status, allowed while no AIF request exists. */
    async updateArticlePublication(id, { postStatus, scheduledAt }) {
      try {
        const response = await axiosPatch(
          `${ARTICLES_ENDPOINT}/${id}/publication`,
          { postStatus, scheduledAt: scheduledAt || undefined },
          { skipErrorSnackbar: true }
        );
        return requireArticle(
          response,
          "article_publication_failed",
          "Le serveur n'a pas renvoyé le brouillon RMR."
        );
      } catch (error) {
        throw serviceError(
          error,
          "article_publication_failed",
          "La publication demandée n'a pas pu être modifiée."
        );
      }
    },
    async listOpportunities({ page = 1, limit = 10, search, shop } = {}) {
      try {
        const response = await axiosGet(
          OPPORTUNITIES_ENDPOINT,
          {
            active: true,
            page,
            limit,
            search: search || undefined,
            shop: shop || undefined,
          },
          { skipErrorSnackbar: true }
        );
        if (!Array.isArray(response?.data?.items)) {
          throw new CtcServiceError(
            "opportunities_failed",
            "La réponse du serveur ne contient pas d'opportunités exploitables."
          );
        }
        return {
          ...response.data,
          items: response.data.items.map(toUiOpportunity),
        };
      } catch (error) {
        throw serviceError(
          error,
          "opportunities_failed",
          "Les opportunités Affilizz n'ont pas pu être chargées."
        );
      }
    },
  };
}
